import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, Copy, ExternalLink, Unlink, CheckCircle2, RotateCcw } from "lucide-react";

export default function TelegramSettings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["telegram-config"],
    queryFn: () => api.get("/telegram/config").then(r => r.data),
    refetchInterval: (query) => (query?.state?.data?.linked ? false : 4000),
  });

  const testMut = useMutation({
    mutationFn: async () => {
      const response = await api.post("/telegram/test");
      if (!response.data?.ok) {
        throw new Error(response.data?.description || response.data?.error || "Telegram rejected the message");
      }
      return response.data;
    },
    onSuccess: () => toast.success("Test message sent — check Telegram"),
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Test failed"),
  });

  const unlinkMut = useMutation({
    mutationFn: () => api.post("/telegram/unlink"),
    onSuccess: () => { toast.success("Telegram unlinked · fresh code generated"); qc.invalidateQueries({queryKey:["telegram-config"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const regenMut = useMutation({
    mutationFn: () => api.post("/telegram/regenerate-code"),
    onSuccess: () => { toast.success("New code generated — use it to link"); qc.invalidateQueries({queryKey:["telegram-config"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  if (q.isLoading) return <Layout title="Telegram."><div className="text-zinc-500 text-sm">Loading…</div></Layout>;

  if (!q.data?.configured) {
    return (
      <Layout title="Telegram." subtitle="Notifications">
        <div className="border border-[#FF3B30]/40 p-6 text-zinc-300 text-sm">
          Telegram bot is not configured on the server. Ask your admin to set <code className="font-mono text-[#FF3B30]">TELEGRAM_BOT_TOKEN</code>.
        </div>
      </Layout>
    );
  }

  const linked = q.data.linked;
  const cmd = `/start ${q.data.link_code}`;

  return (
    <Layout title="Telegram." subtitle="Real-time task notifications">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Status */}
        <div className={`border p-6 ${linked ? "border-emerald-500/40" : "border-white/10"}`}>
          <div className="flex items-center gap-2 mb-2">
            <Send className={`w-4 h-4 ${linked ? "text-emerald-400" : "text-zinc-500"}`}/>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// status</div>
          </div>
          <h3 className="font-display font-black text-2xl tracking-tight mb-3">
            {linked ? "Connected." : "Not connected."}
          </h3>
          {linked ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-4 h-4 text-emerald-400"/>
                <div className="text-[13px] text-zinc-200">
                  Linked as <span className="font-mono text-emerald-300">@{q.data.telegram_username || "user"}</span>
                </div>
              </div>
              <p className="text-[13px] text-zinc-400 leading-relaxed mb-5">
                You'll receive Telegram messages for: new campaign deployments, verification failures on your tasks, and daily drift reminders.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => testMut.mutate()}
                  disabled={testMut.isPending}
                  data-testid="tg-test-btn"
                  className="flex-1 border border-white/15 hover:border-white/40 px-3 py-2 text-[12px] font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {testMut.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
                  SEND TEST MESSAGE
                </button>
                <button
                  onClick={() => { if (window.confirm("Unlink Telegram?")) unlinkMut.mutate(); }}
                  disabled={unlinkMut.isPending}
                  data-testid="tg-unlink-btn"
                  className="border border-[#FF3B30]/40 hover:border-[#FF3B30] text-[#FF3B30] px-3 py-2 text-[12px] font-medium flex items-center gap-2"
                >
                  <Unlink className="w-4 h-4"/> Unlink
                </button>
              </div>
            </>
          ) : (
            <p className="text-[13px] text-zinc-400 leading-relaxed">
              Follow the two-step guide on the right to link your Telegram account to ThreadOps.
              This page will auto-refresh when the link is complete.
            </p>
          )}
        </div>

        {/* Instructions */}
        {!linked && (
          <div className="border border-white/10 p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// two-step link</div>
            <h3 className="font-display font-black text-2xl tracking-tight mb-6">Connect in 30s.</h3>

            <ol className="space-y-6">
              <li>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-mono text-[#FF3B30] text-lg font-bold">01</span>
                  <span className="text-[14px] text-zinc-200">Open ThreadOps bot on Telegram</span>
                </div>
                <a
                  href={q.data.deep_link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="tg-open-bot-link"
                  className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 px-4 flex items-center justify-center gap-2 text-[13px]"
                >
                  <ExternalLink className="w-4 h-4"/>
                  OPEN @{q.data.bot_username} IN TELEGRAM
                </a>
                <div className="text-[11px] font-mono text-zinc-500 mt-2 leading-relaxed">
                  Telegram will open the bot chat. You must then tap <b className="text-zinc-300">START</b> to send the secure linking command; opening the chat alone does not connect your account.
                </div>

                {/* Web fallbacks — legacy telegram.me + t.me — for users whose ISPs block one but not the other */}
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  <a
                    href={q.data.legacy_deep_link || `https://telegram.me/${q.data.bot_username}?start=${q.data.link_code}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="tg-open-bot-web-legacy"
                    className="border border-white/15 hover:border-white/40 text-zinc-200 py-2 px-3 flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-widest"
                  >
                    <ExternalLink className="w-3 h-3"/> telegram.me link
                  </a>
                  <a
                    href={q.data.deep_link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="tg-open-bot-web-tme"
                    className="border border-white/15 hover:border-white/40 text-zinc-200 py-2 px-3 flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-widest"
                  >
                    <ExternalLink className="w-3 h-3"/> t.me link
                  </a>
                </div>
                <div className="text-[10px] font-mono text-amber-400 mt-2 leading-relaxed">
                  ⚠ Some Indian ISPs block <b>t.me</b>. If you see <b>DNS_PROBE_FINISHED_NXDOMAIN</b>, try the telegram.me link or open the app directly — or use Step 02 below to paste the command manually.
                </div>
              </li>
              <li>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-mono text-[#FF3B30] text-lg font-bold">02</span>
                  <span className="text-[14px] text-zinc-200">If no START button appears, send this manually to <span className="font-mono text-white">@{q.data.bot_username}</span></span>
                </div>
                <div className="bg-[#050505] border border-white/10 p-3 font-mono text-[13px] flex items-center justify-between gap-2">
                  <span className="text-emerald-300 truncate" data-testid="tg-cmd-display">{cmd}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(cmd); toast.success("Copied"); }}
                    data-testid="tg-copy-cmd-btn"
                    className="border border-white/10 hover:border-white/30 px-2 py-1 text-[10px] font-mono flex items-center gap-1 shrink-0"
                  >
                    <Copy className="w-3 h-3"/> COPY
                  </button>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-[11px] text-zinc-500 leading-relaxed">
                    Not working? Your code may have been consumed by a previous link attempt. Regenerate a fresh one below.
                  </div>
                  <button
                    onClick={() => regenMut.mutate()}
                    disabled={regenMut.isPending}
                    data-testid="tg-regen-code-btn"
                    className="shrink-0 border border-amber-500/30 hover:border-amber-500 text-amber-300 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {regenMut.isPending ? <Loader2 className="w-3 h-3 animate-spin"/> : <RotateCcw className="w-3 h-3"/>}
                    Regenerate
                  </button>
                </div>
              </li>
            </ol>
          </div>
        )}
      </div>
    </Layout>
  );
}
